// src/resources/calendars.js - Updated with status checking
import * as React from 'react';
import {
    List,
    Datagrid,
    TextField,
    Edit,
    Create,
    SimpleForm,
    TextInput,
    required,
    useRecordContext,
    TopToolbar,
    RefreshButton,
    CreateButton,
    ExportButton,
    useListContext,
    SimpleList,
} from 'react-admin';
import { regex } from 'react-admin';
import { Box, Typography, Tooltip, Chip, IconButton, CircularProgress, Paper, Grid, useMediaQuery, useTheme } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import RefreshIcon from '@mui/icons-material/Refresh';
import EventIcon from '@mui/icons-material/Event';
import LinkIcon from '@mui/icons-material/Link';
import { useCalendarStatus } from '../hooks/useCalendarStatus';
import { useResourceLimits, useResourceLimitMessage } from '../hooks/useResourceLimits';

// URL validation regex
const urlValidation = regex(
    /^(http|https):\/\/[^ "]+$/,
    'Must be a valid URL starting with http:// or https://'
);

// Custom field to display URLs with word breaks
const UrlField = ({ source }) => {
    const record = useRecordContext();
    
    if (!record || !record[source]) return null;
    
    return (
        <Tooltip title={record[source]} placement="top">
            <Box 
                sx={{ 
                    maxWidth: 300,
                    wordBreak: 'break-word',
                    overflowWrap: 'break-word',
                    '& a': {
                        color: 'primary.main',
                        textDecoration: 'none',
                        '&:hover': {
                            textDecoration: 'underline'
                        }
                    }
                }}
            >
                <a href={record[source]} target="_blank" rel="noopener noreferrer">
                    {record[source]}
                </a>
            </Box>
        </Tooltip>
    );
};

// Calendar status component that uses useRecordContext internally
const CalendarStatus = () => {
    const record = useRecordContext();
    const teamId = localStorage.getItem('userTeamId');
    
    // Always call the hook, but conditionally enable it based on whether we have a valid record
    const { 
        data: statusData, 
        isLoading, 
        refetch,
        isFetching
    } = useCalendarStatus(teamId, record?.id, {
        // Only enable the query if we have a valid record with an ID
        enabled: !!record && !!record.id
    });
    
    // If we don't have a valid record, show unavailable status
    if (!record || !record.id) {
        return (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="body2" color="textSecondary">
                    Status unavailable
                </Typography>
            </Box>
        );
    }
    
    if (isLoading || isFetching) {
        return (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <CircularProgress size={24} />
            </Box>
        );
    }
    
    const isSuccess = statusData?.status === 'success';
    
    return (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Chip
                icon={isSuccess ? <CheckCircleIcon /> : <ErrorIcon />}
                label={statusData?.message || (isSuccess ? 'Available' : 'Error')}
                color={isSuccess ? 'success' : 'error'}
                size="small"
                sx={{ mr: 1 }}
            />
            <IconButton 
                onClick={(e) => {
                    e.stopPropagation();
                    refetch();
                }} 
                size="small"
                title="Refresh status"
            >
                <RefreshIcon fontSize="small" />
            </IconButton>
        </Box>
    );
};

// List actions component with refresh, create, and export buttons
const ListActions = () => {
    const { refetch } = useListContext();
    const { data: limitsData, invalidate } = useResourceLimits('calendars');
    const limitMessage = useResourceLimitMessage('calendars');
    
    // Invalidate limits when the list might have changed
    React.useEffect(() => {
        const handleStorageChange = () => {
            invalidate();
        };
        
        window.addEventListener('storage', handleStorageChange);
        return () => window.removeEventListener('storage', handleStorageChange);
    }, [invalidate]);
    
    const createButton = (
        <CreateButton 
            disabled={limitsData && !limitsData.canCreate}
            sx={{
                ...(limitsData && !limitsData.canCreate && {
                    opacity: 0.5,
                    cursor: 'not-allowed'
                })
            }}
        />
    );
    
    const handleRefresh = () => {
        refetch();
        invalidate();
    };
    
    return (
        <TopToolbar>
            <RefreshButton onClick={handleRefresh} />
            {limitsData && !limitsData.canCreate ? (
                <Tooltip title={limitMessage} placement="top">
                    <span>{createButton}</span>
                </Tooltip>
            ) : (
                createButton
            )}
            <ExportButton />
        </TopToolbar>
    );
};

// Custom mobile list item component
const MobileCalendarItem = () => {
    const record = useRecordContext();
    if (!record) return null;

    return (
        <Box sx={{ py: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <EventIcon sx={{ mr: 1, color: 'primary.main', fontSize: 20 }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                    {record.fullname}
                </Typography>
            </Box>
            <Box sx={{ ml: 3.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
                    <LinkIcon sx={{ mr: 0.5, mt: 0.3, color: 'text.secondary', fontSize: 16 }} />
                    <Typography
                        variant="body2"
                        sx={{
                            wordBreak: 'break-all',
                            color: 'text.secondary',
                            flex: 1
                        }}
                    >
                        {record.url}
                    </Typography>
                </Box>
                <CalendarStatus />
            </Box>
        </Box>
    );
};

export const CalendarList = () => {
    const theme = useTheme();
    const isSmall = useMediaQuery(theme.breakpoints.down('md'));

    return (
        <List
            bulkActionButtons={false}
            sort={{ field: 'fullname', order: 'ASC' }}
            actions={<ListActions />}
        >
            {isSmall ? (
                // Mobile view - SimpleList with custom component
                <SimpleList
                    linkType="edit"
                    primaryText={<MobileCalendarItem />}
                    sx={{
                        '& .MuiListItem-root': {
                            display: 'block',
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 1,
                            mb: 1,
                            px: 2,
                            py: 1,
                            '&:hover': {
                                backgroundColor: 'action.hover'
                            }
                        },
                        '& .MuiListItemText-root': {
                            margin: 0
                        },
                        '& .MuiListItemText-primary': {
                            typography: 'body1'
                        }
                    }}
                />
            ) : (
                // Desktop view - datagrid
                <Datagrid
                    rowClick="edit"
                    bulkActionButtons={false}
                    sx={{
                        '& [data-field="__selection__"]': { display: 'none' },
                        '& .MuiTableCell-paddingCheckbox': { display: 'none' },
                        // Making column headers bold
                        '& .MuiTableCell-head': {
                            fontWeight: 'bold',
                        }
                    }}
                >
                    <TextField source="fullname" sortable={true} />
                    <UrlField source="url" sortable={true} />
                    <CalendarStatus label="Status" />
                </Datagrid>
            )}
        </List>
    );
};

const CalendarTitle = ({ record }) => {
    return <span>Calendar: {record ? record.fullname : ''}</span>;
};

export const CalendarEdit = () => {
    const record = useRecordContext();
    
    return (
        <Edit title={<CalendarTitle />} mutationMode="pessimistic">
            <SimpleForm>
                <TextInput source="fullname" validate={[required()]} fullWidth />
                <TextInput 
                    source="url" 
                    validate={[required(), urlValidation]} 
                    fullWidth 
                    helperText="URL must start with http:// or https://"
                    multiline
                    rows={3}
                />
                {record && record.id && (
                    <Box mt={2}>
                        <Typography variant="subtitle2" gutterBottom>
                            Calendar Status:
                        </Typography>
                        <CalendarStatus />
                    </Box>
                )}
            </SimpleForm>
        </Edit>
    );
};

export const CalendarCreate = () => (
    <Create>
        <SimpleForm>
            <TextInput source="fullname" validate={[required()]} fullWidth />
            <TextInput 
                source="url" 
                validate={[required(), urlValidation]} 
                fullWidth 
                helperText="URL must start with http:// or https://"
                multiline
                rows={3}
            />
        </SimpleForm>
    </Create>
);